import { cn } from '@repo/tailwind-config/tailwind.functions'
import { StringFormatter } from '@repo/toolkit/string'
import { FileTypeEnum } from '@repo/toolkit/types'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { Column } from '@repo/ui/data-table'
import { CheckMarkIcon, DocumentIcon, XIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME, MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import React, { Fragment } from 'react'

import { DisplayImage } from '../../../../../packages/ui/components/atoms/input/file/display-image'
import { API } from '../../api/apis.config'
import { invalidateStaffsDocuments } from '../../api/staff/invalidate-staff-document'
import { validateStaffsDocuments } from '../../api/staff/validate-staff-document'
import DynamicDataTable, { DynamicTableRef } from '../../components/elements/dynamic-table'
import UserNavBar from '../../components/elements/nav-bar'
import StaffDocumentPreview from '../../components/pages/staff/documents-validation/StaffDocumentPreview'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import useTranslate from '../../lib/hooks/use-translate'
import { staffDocumentTypeEnumMapping } from '../../lib/types/enums/common/document-status.enum'
import { staffPositionEnumMapping } from '../../lib/types/enums/staff/staff-position.enum'
import { StaffDocument } from '../../lib/types/models/staff/staff'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'

const StaffDocumentListPage = () => {
  const tableRef = React.useRef<DynamicTableRef<StaffDocument>>(null)
  const { t } = useTranslate()

  const { modalState, selectedData, toggleModal, closeModel } = useModalManager<
    'image-preview' | 'pdf-preview' | 'validate-document' | 'invalidate-document',
    StaffDocument
  >(['image-preview', 'pdf-preview', 'validate-document', 'invalidate-document'])

  const { mutate: validateStaffsDocumentsMutation, isPending: isValidateStaffsDocumentsPending } =
    useMutation({
      mutationFn: validateStaffsDocuments,
      onError: showAxiosError,
      onSuccess: () => {
        tableRef.current?.refetch()
        closeModel('validate-document')
      }
    })

  const {
    mutate: invalidateStaffsDocumentsMutation,
    isPending: isInvalidateStaffsDocumentsPending
  } = useMutation({
    mutationFn: invalidateStaffsDocuments,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('invalidate-document')
    }
  })

  const columns: Column<StaffDocument>[] = [
    {
      accessorKey: 'lastName',
      header: 'NOM',
      cell: ({ row }) => row.original.staff.lastName.toUpperCase()
    },
    {
      accessorKey: 'firstName',
      header: 'Prénom',
      cell: ({ row }) => StringFormatter.capitalize(row.original.staff.firstName)
    },
    {
      accessorKey: 'position',
      header: 'Poste',
      cell: ({ row }) => t(staffPositionEnumMapping[row.original.staff.position])
    },
    {
      accessorKey: 'type',
      header: 'Document',
      cell: ({ row }) => (
        <Badge
          text={staffDocumentTypeEnumMapping(row.original, t).label}
          variant={staffDocumentTypeEnumMapping(row.original, t).variant}
        />
      )
    },
    {
      accessorKey: 'file',
      header: 'Aperçu',
      cell: ({ row }) => (
        <DisplayImage
          type={row.original.file.type}
          src={row.original.file.path}
          className={'w-12 h-10 rounded-sm cursor-pointer object-cover object-center'}
          onImagePreview={() => {
            if (row.original.file.type === FileTypeEnum.IMAGE) {
              toggleModal('image-preview', row.original)
            } else if (row.original.file.type === FileTypeEnum.PDF) {
              toggleModal('pdf-preview', row.original)
            }
          }}
        />
      )
    },
    {
      accessorKey: 'id',
      header: '',
      cell: ({ row }) => (
        <div className={'flex gap-1'}>
          <Button
            variant={'destructive'}
            className={'p-1 h-fit rounded-sm bg-red-light hover:bg-red'}
            icon={XIcon}
            onClick={() => toggleModal('invalidate-document', row.original)}
          />
          <Button
            variant={'success'}
            className={'p-1 h-fit rounded-sm bg-green-light hover:bg-green'}
            icon={CheckMarkIcon}
            onClick={() => toggleModal('validate-document', row.original)}
          />
        </div>
      )
    }
  ]

  return (
    <div>
      <UserNavBar title={'Documents à valider'} icon={DocumentIcon} />
      <div className={'h-4'} />
      <DynamicDataTable<StaffDocument>
        ref={tableRef}
        title={'Tous les documents'}
        deleteMessage={{
          name: 'documents',
          attributeName: 'id',
          deleteOne: 'Êtes-vous sûr de vouloir supprimer le document ?',
          deleteMany: 'Êtes-vous sûr de vouloir supprimer ces documents ?',
          deleteAll: 'Êtes-vous sûr de vouloir supprimer tous les documents ?'
        }}
        columns={columns}
        allowSearchParam
        permissions={{ delete: false }}
        endpoint={API.staffs.getAllDocuments.url}
      />

      {selectedData && (
        <Fragment>
          <Modal
            isOpen={modalState['image-preview']}
            title={'Document'}
            icon={DocumentIcon}
            size="medium"
            hideActions
            onCancel={() => closeModel('image-preview')}
          >
            <div className={cn('flex justify-center flex-col gap-3', MODAL_CONTENT_CLASSNAME)}>
              <Badge
                text={staffDocumentTypeEnumMapping(selectedData, t).label}
                variant={staffDocumentTypeEnumMapping(selectedData, t).variant}
              />
              <DisplayImage
                type={selectedData.file.type}
                src={selectedData.file.path}
                className={'rounded-xl'}
              />
            </div>
          </Modal>

          <Modal
            actionButtonsOptions={{
              confirmText: 'Valider',
              onConfirm: () => validateStaffsDocumentsMutation(selectedData.id),
              cancelText: 'Fermer'
            }}
            isOpen={modalState['validate-document']}
            icon={CheckMarkIcon}
            variant={'confirmation'}
            isLoading={isValidateStaffsDocumentsPending}
            onCancel={() => closeModel('validate-document')}
          >
            <div className={MODAL_BODY_CLASSNAME}>
              <StaffDocumentPreview
                document={selectedData}
                title={`Êtes-vous sûr de vouloir valider le document '${staffDocumentTypeEnumMapping(selectedData, t).label}' ?`}
              />
            </div>
          </Modal>

          <Modal
            isOpen={modalState['pdf-preview']}
            title={'PDF Preview'}
            size="large"
            hideActions
            onCancel={() => closeModel('pdf-preview')}
          >
            <iframe src={selectedData.file.path} width="100%" height="600px" title="PDF Preview" />
          </Modal>

          <Modal
            actionButtonsOptions={{
              confirmText: 'Rejeter',
              cancelText: 'Annuler',
              onConfirm: () => invalidateStaffsDocumentsMutation(selectedData.id)
            }}
            isOpen={modalState['invalidate-document']}
            icon={XIcon}
            variant={'destructive'}
            isLoading={isInvalidateStaffsDocumentsPending}
            onCancel={() => closeModel('invalidate-document')}
          >
            <div className={MODAL_BODY_CLASSNAME}>
              <StaffDocumentPreview
                document={selectedData}
                title={`Êtes-vous sûr de vouloir rejeter le document '${staffDocumentTypeEnumMapping(selectedData, t).label}' ?`}
              />
            </div>
          </Modal>
        </Fragment>
      )}
    </div>
  )
}

export default StaffDocumentListPage
